import posthog from 'posthog-js'

// PostHog configuration
const POSTHOG_KEY = import.meta.env.VITE_PUBLIC_POSTHOG_KEY
const POSTHOG_HOST = import.meta.env.VITE_PUBLIC_POSTHOG_HOST || 'https://us.i.posthog.com'

// Initialize PostHog
export function initPostHog() {
  if (!POSTHOG_KEY) {
    console.warn('[PostHog] No API key found. Analytics disabled.')
    return
  }

  posthog.init(POSTHOG_KEY, {
    api_host: POSTHOG_HOST,
    // Capture page views automatically
    capture_pageview: true,
    // Capture page leaves
    capture_pageleave: true,
    // Enable session recordings (optional - can be disabled)
    disable_session_recording: false,
    // Respect Do Not Track
    respect_dnt: true,
    // Persistence method
    persistence: 'localStorage',
    // Autocapture clicks, inputs, etc.
    autocapture: true,
    // Don't capture in development by default
    loaded: () => {
      if (import.meta.env.DEV) {
        // Optionally disable in development
        // posthog.opt_out_capturing()
        console.log('[PostHog] Initialized in development mode')
      }
    }
  })
}

// Identify user after login
export function identifyUser(userId: string, properties?: Record<string, unknown>) {
  if (!POSTHOG_KEY) return

  posthog.identify(userId, properties)
}

// Reset user on logout
export function resetUser() {
  if (!POSTHOG_KEY) return

  posthog.reset()
}

// Track custom events
export function trackEvent(eventName: string, properties?: Record<string, unknown>) {
  if (!POSTHOG_KEY) return

  posthog.capture(eventName, properties)
}

// Track page view (for SPA navigation)
export function trackPageView(path?: string) {
  if (!POSTHOG_KEY) return

  posthog.capture('$pageview', {
    $current_url: path || window.location.href
  })
}

// Set user properties without identifying
export function setUserProperties(properties: Record<string, unknown>) {
  if (!POSTHOG_KEY) return

  posthog.people.set(properties)
}

// Check if PostHog is initialized
export function isPostHogEnabled() {
  return !!POSTHOG_KEY
}

// Export posthog instance for direct access if needed
export { posthog }

// Predefined event names for consistency
export const ANALYTICS_EVENTS = {
  // Auth events
  USER_SIGNED_UP: 'user_signed_up',
  USER_LOGGED_IN: 'user_logged_in',
  USER_LOGGED_OUT: 'user_logged_out',

  // Onboarding events
  ONBOARDING_STARTED: 'onboarding_started',
  ONBOARDING_STEP_COMPLETED: 'onboarding_step_completed',
  ONBOARDING_COMPLETED: 'onboarding_completed',

  // Brand events
  BRAND_CREATED: 'brand_created',
  BRAND_UPDATED: 'brand_updated',
  BRAND_DELETED: 'brand_deleted',

  // Subscription events
  SUBSCRIPTION_STARTED: 'subscription_started',
  SUBSCRIPTION_UPGRADED: 'subscription_upgraded',
  SUBSCRIPTION_CANCELED: 'subscription_canceled',
  TRIAL_STARTED: 'trial_started',

  // Feature usage events
  SEARCH_QUERY_ADDED: 'search_query_added',
  SEARCH_QUERY_ANALYZED: 'search_query_analyzed',
  COMPETITOR_ADDED: 'competitor_added',
  COMPETITOR_REMOVED: 'competitor_removed',
  LLMS_TXT_GENERATED: 'llms_txt_generated',
  CONTENT_IMPORTED: 'content_imported',

  // Engagement events
  DEMO_VIEWED: 'demo_viewed',
  DASHBOARD_VIEWED: 'dashboard_viewed',
  INSIGHTS_VIEWED: 'insights_viewed',
  COMPETITION_VIEWED: 'competition_viewed',

  // CTA events
  CTA_CLICKED: 'cta_clicked',
  PRICING_VIEWED: 'pricing_viewed',
  CONTACT_FORM_SUBMITTED: 'contact_form_submitted'
} as const
